import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  Assessment as ReportIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { reportsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const SubmissionReport: React.FC = () => {
  const { showSuccess, showError, showWarning } = useToast();
  
  // State
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Date preset handlers
  const handlePresetDateRange = (preset: 'lastMonth' | 'lastQuarter' | 'thisYear') => {
    const today = new Date();
    let start: Date;
    let end: Date;

    switch (preset) {
      case 'lastMonth':
        // Last month: First day to last day of previous month
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      
      case 'lastQuarter':
        // Last quarter: 3 months ago to end of last month
        start = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      
      case 'thisYear':
        // This year: January 1 to today
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setError(null);
  };

  // Validate date range
  const validateDates = (): boolean => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return false;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      setError('Start date must be before end date.');
      return false;
    }

    if (end > new Date()) {
      setError('End date cannot be in the future.');
      return false;
    }

    setError(null);
    return true;
  };

  // Generate report
  const handleGenerateReport = async () => {
    if (!validateDates()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call API to generate Excel file
      await reportsAPI.generateSubmissionsReport(startDate, endDate);
      
      showSuccess('Report generated successfully!');
    } catch (err: any) {
      console.error('Error generating report:', err);
      
      if (err.response?.status === 404) {
        setError('No completed submissions found in the specified date range.');
        showWarning('No submissions found for the selected period.');
      } else {
        setError(err.response?.data?.message || 'Failed to generate report. Please try again.');
        showError('Failed to generate report.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReportIcon fontSize="large" />
          Generate Expense Report
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate a detailed Excel report of all completed expense submissions for a specific date range.
        </Typography>
      </Box>

      {/* Instructions Card */}
      <Card sx={{ mb: 3, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Report Details
          </Typography>
          <Typography variant="body2" component="div">
            • Only <strong>completed</strong> submissions (approved by both Manager and Admin) are included
            <br />
            • Report is grouped by program name and sorted by employee name
            <br />
            • Includes: Employee name, program details, allowances, amounts, and approval dates
            <br />
            • Downloads as an Excel (.xlsx) file for easy printing and sharing
          </Typography>
        </CardContent>
      </Card>

      {/* Date Range Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon />
          Select Date Range
        </Typography>

        {/* Quick Presets */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Quick Select:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip
              label="Last Month"
              onClick={() => handlePresetDateRange('lastMonth')}
              color={!startDate && !endDate ? 'default' : 'primary'}
              variant={!startDate && !endDate ? 'outlined' : 'filled'}
            />
            <Chip
              label="Last Quarter"
              onClick={() => handlePresetDateRange('lastQuarter')}
              color={!startDate && !endDate ? 'default' : 'primary'}
              variant={!startDate && !endDate ? 'outlined' : 'filled'}
            />
            <Chip
              label="This Year"
              onClick={() => handlePresetDateRange('thisYear')}
              color={!startDate && !endDate ? 'default' : 'primary'}
              variant={!startDate && !endDate ? 'outlined' : 'filled'}
            />
          </Stack>
        </Box>

        {/* Custom Date Pickers */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setError(null);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().split('T')[0], // Can't select future dates
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setError(null);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().split('T')[0], // Can't select future dates
              }}
            />
          </Grid>
        </Grid>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Generate Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleGenerateReport}
            disabled={loading || !startDate || !endDate}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          >
            {loading ? 'Generating Report...' : 'Generate Excel Report'}
          </Button>
        </Box>
      </Paper>

      {/* Help Text */}
      <Card sx={{ bgcolor: 'grey.50' }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • The report will download automatically as an Excel file when generation is complete
            <br />
            • If no submissions are found, you'll see a message and no file will be generated
            <br />
            • Report generation typically takes 5-10 seconds for large date ranges
            <br />
            • File name format: Expense_Report_YYYY-MM-DD_to_YYYY-MM-DD.xlsx
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SubmissionReport;